import { LOCATION_TYPE } from '../location/constants'
import { getTopFiveAndSortRemainingItemsByLabel } from './useCases/getTopFiveAndSortRemainingItemsByLabel'
import { sortDataWithManualTopConditions } from './useCases/sortDataWithManualTopConditions'
import { DistanceObjectArray } from '@/types/DistanceOptions'

export const FILTER_RESULTS_PER_PAGE = 15

export const PRICE_OPTIONS = [
  'Less than $10,000 ',
  '$10,000 to $20,000',
  '$10,000 to $25,000',
  '$25,000 to $50,000',
  'Over $50,000',
  'Over $75,000'
]

export const SMOKING_POLICY_DISPLAY_LABELS = {
  PERMITTED_ANYWHERE: 'Smoking Allowed',
  PERMITTED_DESIGNATED_AREA: 'Smoking Allowed in Designated Areas',
  NOT_PERMITTED: 'Smoking Not Allowed'
}

export const VAPING_POLICY_DISPLAY_LABELS = {
  PERMITTED_ANYWHERE: 'Vaping Allowed',
  PERMITTED_DESIGNATED_AREA: 'Vaping Allowed in Designated Areas',
  NOT_PERMITTED: 'Vaping Not Allowed'
}

export const UNITED_STATES_OPTION_NAME = '1500 Miles'
export const WORLDWIDE_SEARCH_LOCATION_NAME = 'Worldwide'
export const WORLDWIDE_SEARCH_DISTANCE_FROM_GEOLOCATION_TEXT = 'Any Distance'

export const DISTANCE_OPTIONS: DistanceObjectArray = {
  US: [
    { value: 5, meters: 8047, unit: 'Miles' },
    { value: 15, meters: 24140, unit: 'Miles' },
    { value: 60, meters: 96561, unit: 'Miles' },
    { value: 100, meters: 160934, unit: 'Miles' },
    { value: 500, meters: 804672, unit: 'Miles' },
    { value: WORLDWIDE_SEARCH_LOCATION_NAME, meters: null, unit: null }
  ],
  Global: [
    { value: 10, meters: 10000, unit: 'km' },
    { value: 25, meters: 25000, unit: 'km' },
    { value: 100, meters: 100000, unit: 'km' },
    { value: 150, meters: 150000, unit: 'km' },
    { value: 800, meters: 800000, unit: 'km' },
    { value: WORLDWIDE_SEARCH_LOCATION_NAME, meters: null, unit: null }
  ]
}

const DISTANCE_OPTION_INDEX = DISTANCE_OPTIONS.US.reduce(
  (acc, option, index) => {
    acc[`OPTION_${index + 1}`] = index
    return acc
  },
  {}
) as {
  [key: `OPTION_${number}`]: number
}

export const DISTANCE_OPTIONS_LOCATION_TYPE_MAPPING = {
  [LOCATION_TYPE.CITY]: DISTANCE_OPTION_INDEX.OPTION_3,
  [LOCATION_TYPE.REGION]: DISTANCE_OPTION_INDEX.OPTION_4,
  [LOCATION_TYPE.COUNTRY]: DISTANCE_OPTION_INDEX.OPTION_5,
  [LOCATION_TYPE.CONTINENT]: DISTANCE_OPTION_INDEX.OPTION_6
}

export const DEFAULT_LOCATION_TYPE = LOCATION_TYPE.CITY

export const DEFAULT_DISTANCE_INDEX =
  DISTANCE_OPTIONS_LOCATION_TYPE_MAPPING[DEFAULT_LOCATION_TYPE]

export const DEFAULT_AROUND_RADIUS_IN_METERS = DISTANCE_OPTIONS.US[
  DEFAULT_DISTANCE_INDEX
].meters as number

export const DEFAULT_AROUND_RADIUS_IN_MILES = DISTANCE_OPTIONS.US[
  DEFAULT_DISTANCE_INDEX
].value as number

export const FILTER_SELECTORS_LABELS = {
  LOCATIONS: 'Locations',
  CONDITIONS: 'Conditions',
  INSURANCE: 'Insurance',
  TREATMENT_SERVICES: 'Levels of Care',
  TREATMENT_THERAPIES: 'Treatment Therapies',
  SMOKING_POLICY: 'Smoking Policy',
  VAPING_POLICY: 'Vaping Policy',
  LUXURY: 'Luxury',
  PRICE: 'Price',
  CLIENTELE: 'Clientele',
  SPECIAL_CONSIDERATIONS: 'Special Considerations',
  APPROACH: 'Approach',
  SETTING: 'Setting',
  AMENITIES: 'Amenities',
  ACTIVITIES: 'Activities',
  LANGUAGES: 'Languages',
  BRANDS: 'Brands'
}

export const FACET_PAGE_FILTERS = [
  'locations',
  'what_we_treat',
  'who_we_treat',
  'treatment_methods',
  'amenities',
  'aftercare',
  'price',
  'treatment_services',
  'accepted_insurance',
  'smoking_policy',
  'vaping_policy'
]

export const FACET_PAGE_EXCLUDED_ATTRIBUTES = [
  'brand_title',
  'smoking_policy',
  'vaping_policy'
]

export const FILTER_SELECTORS_STATIC_DATA = [
  {
    attribute: 'locations',
    label: FILTER_SELECTORS_LABELS.LOCATIONS,
    multiSelect: false,
    operator: 'and',
    binary: false,
    searchable: true,
    icons: {},
    extractData: (items) => items,
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'what_we_treat',
    label: FILTER_SELECTORS_LABELS.CONDITIONS,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => sortDataWithManualTopConditions(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'accepted_insurance',
    taxonomyType: 'accepted_insurance',
    label: FILTER_SELECTORS_LABELS.INSURANCE,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => {
      return items.sort((a, b) => a.label.localeCompare(b.label))
    },
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'treatment_services',
    label: FILTER_SELECTORS_LABELS.TREATMENT_SERVICES,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  // {
  //   attribute: 'smoking_policy',
  //   label: FILTER_SELECTORS_LABELS.SMOKING_POLICY,
  //   multiSelect: false,
  //   operator: 'and',
  //   searchable: false,
  //   binary: false,
  //   icons: {},
  //   extractData: (items) => items,
  //   displayLabelMapping: SMOKING_POLICY_DISPLAY_LABELS,
  //   itemWhitelist: [],
  //   itemBlacklist: []
  // },
  {
    attribute: 'treatment_therapies',
    label: FILTER_SELECTORS_LABELS.TREATMENT_THERAPIES,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'amenities',
    label: FILTER_SELECTORS_LABELS.LUXURY,
    multiSelect: true,
    operator: 'and',
    searchable: false,
    binary: true,
    refinement: 'Luxury',
    icons: {},
    extractData: (items) => items,
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'price',
    label: FILTER_SELECTORS_LABELS.PRICE,
    multiSelect: true,
    operator: 'and',
    searchable: false,
    binary: false,
    icons: {},
    extractData: (items) =>
      items.sort(
        (a, b) =>
          PRICE_OPTIONS.indexOf(a.label) - PRICE_OPTIONS.indexOf(b.label)
      ),
    itemWhitelist: [],
    itemBlacklist: ['Insurance Accepted']
  },
  {
    attribute: 'who_we_treat',
    label: FILTER_SELECTORS_LABELS.CLIENTELE,
    multiSelect: true,
    searchable: true,
    operator: 'and',
    binary: false,
    icons: {},
    extractData: (items): string[] | [] => items,
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'brand_title',
    taxonomyType: 'brand_title',
    label: FILTER_SELECTORS_LABELS.BRANDS,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => {
      return items.sort((a, b) => a.label.localeCompare(b.label))
    },
    itemWhitelist: [],
    itemBlacklist: []
  },
  // {
  //   attribute: 'vaping_policy',
  //   label: FILTER_SELECTORS_LABELS.VAPING_POLICY,
  //   multiSelect: false,
  //   operator: 'and',
  //   searchable: false,
  //   binary: false,
  //   icons: {},
  //   extractData: (items) => items,
  //   displayLabelMapping: VAPING_POLICY_DISPLAY_LABELS,
  //   itemWhitelist: [],
  //   itemBlacklist: []
  // },
  {
    attribute: 'special_considerations',
    taxonomyType: 'special_consideration',
    label: FILTER_SELECTORS_LABELS.SPECIAL_CONSIDERATIONS,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'treatment_methods',
    label: FILTER_SELECTORS_LABELS.APPROACH,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'setting',
    label: FILTER_SELECTORS_LABELS.SETTING,
    multiSelect: false,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => items,
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'amenities',
    label: FILTER_SELECTORS_LABELS.AMENITIES,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => items.slice(0, 8),
    itemWhitelist: [],
    itemBlacklist: ['Luxury']
  },
  {
    attribute: 'activities',
    label: FILTER_SELECTORS_LABELS.ACTIVITIES,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  },
  {
    attribute: 'languages',
    taxonomyType: 'language',
    label: FILTER_SELECTORS_LABELS.LANGUAGES,
    multiSelect: true,
    operator: 'and',
    searchable: true,
    binary: false,
    icons: {},
    extractData: (items) => getTopFiveAndSortRemainingItemsByLabel(items),
    itemWhitelist: [],
    itemBlacklist: []
  }
]
